import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axios.config';
import { toast } from 'react-hot-toast';
import ReactQuill from 'react-quill';
import axios from 'axios';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, convertToRaw } from 'draft-js';
import { baseUrl } from '../../utils/url';
import Loading from '../Shared/Loading';

function AddDonorPartner() {
  const [isLoading, setIsLoading] = useState(false);
  const [donorPartner, setDonorPartner] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm();

  // Fetch donor/partner data if editing
  useEffect(() => {
    if (id) {
      const fetchDonorPartner = async () => {
        try {
          setIsLoading(true);
          const { data } = await api.get(`donor-partner/${id}`);
          if (data.success) {
            setDonorPartner(data.donorPartner);
            // Set form values
            setValue('name', data.donorPartner.name);
            setValue('institute', data.donorPartner.institute);
            setValue('designation', data.donorPartner.designation);
            setValue('facebookUrl', data.donorPartner.facebookUrl);
            setValue('emailUrl', data.donorPartner.emailUrl);
            setValue('webUrl', data.donorPartner.webUrl);
          }
        } catch (error) {
          toast.error('Error fetching donor/partner data');
        } finally {
          setIsLoading(false);
        }
      };

      fetchDonorPartner();
    }
  }, [id, setValue]);

  const onSubmit = async ({
    file,
    name,
    institute,
    designation,
    facebookUrl,
    emailUrl,
    webUrl,
  }) => {
    setIsLoading(true);

    const formData = new FormData();

    // Only append image if a new file is selected
    if (file && file[0]) {
      formData.append('image', file[0]);
    }
    formData.append('name', name);
    formData.append('institute', institute);
    formData.append('designation', designation);
    formData.append('facebookUrl', facebookUrl || '');
    formData.append('emailUrl', emailUrl || '');
    formData.append('webUrl', webUrl || '');

    try {
      let response;
      if (id) {
        // Update existing donor/partner
        response = await api.patch(`donor-partner/${id}`, formData);
      } else {
        // Create new donor/partner
        response = await api.post('donor-partner', formData);
      }

      const { data } = response;

      if (data.success) {
        toast.success(data.message);
        reset();
        navigate('/dashboard/donor-partner');
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error(error.response?.data?.error ?? error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <h2 className="text-2xl font-bold mb-4">
        {id ? 'ডোনার/পার্টনার আপডেট করুন' : 'নতুন ডোনার/পার্টনার যুক্ত করুন'}
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div>
            <label className="label">
              <span className="label-text">
                ছবি নির্বাচন করুন {id ? '' : '*'}
              </span>
            </label>
            {id && donorPartner?.imageUrl && (
              <div className="mb-2">
                <img
                  src={`${baseUrl}${donorPartner.imageUrl}`}
                  alt="Current"
                  className="w-32 h-32 object-cover rounded"
                />
                <p className="text-sm text-gray-500 mt-1">
                  বর্তমান ছবি (নতুন ছবি নির্বাচন করলে আপডেট হবে)
                </p>
              </div>
            )}
            <input
              type="file"
              {...register('file', {
                required: id ? false : 'ছবি আবশ্যক',
              })}
              className="file-input file-input-bordered file-input-primary w-full max-w-xs"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.file && errors.file.message}
              </span>
            </label>
          </div>
          <div className="form-control w-full max-w-xs">
            <label className="label">
              <span className="label-text">নাম *</span>
            </label>
            <input
              {...register('name', {
                required: 'নাম আবশ্যক',
              })}
              type="text"
              placeholder="নাম লিখুন"
              className="input input-bordered w-full max-w-xs"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.name && errors.name.message}
              </span>
            </label>
          </div>
          <div className="form-control w-full max-w-xs">
            <label className="label">
              <span className="label-text">প্রতিষ্ঠান *</span>
            </label>
            <input
              {...register('institute', {
                required: 'প্রতিষ্ঠান আবশ্যক',
              })}
              type="text"
              placeholder="প্রতিষ্ঠান লিখুন"
              className="input input-bordered w-full max-w-xs"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.institute && errors.institute.message}
              </span>
            </label>
          </div>
          <div className="form-control w-full max-w-xs">
            <label className="label">
              <span className="label-text">পদবী *</span>
            </label>
            <input
              {...register('designation', {
                required: 'পদবী আবশ্যক',
              })}
              type="text"
              placeholder="পদবী লিখুন"
              className="input input-bordered w-full max-w-xs"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.designation && errors.designation.message}
              </span>
            </label>
          </div>
          <div className="form-control w-full max-w-xs">
            <label className="label">
              <span className="label-text">ফেসবুক প্রোফাইল লিংক </span>
            </label>
            <input
              {...register('facebookUrl')}
              type="text"
              placeholder="ফেসবুক প্রোফাইল লিংক লিখুন"
              className="input input-bordered w-full max-w-xs"
            />
          </div>
          <div className="form-control w-full max-w-xs">
            <label className="label">
              <span className="label-text">ইমেইল লিংক </span>
            </label>
            <input
              {...register('emailUrl')}
              type="text"
              placeholder="ইমেইল লিংক লিখুন"
              className="input input-bordered w-full max-w-xs"
            />
          </div>
          <div className="form-control w-full max-w-xs">
            <label className="label">
              <span className="label-text">ওয়েবসাইট লিংক </span>
            </label>
            <input
              {...register('webUrl')}
              type="text"
              placeholder="ওয়েবসাইট লিংক লিখুন"
              className="input input-bordered w-full max-w-xs"
            />
          </div>
        </div>
        <div className="mt-10 flex gap-4">
          <input
            className="btn btn-primary text-white w-44"
            value={id ? 'আপডেট করুন' : 'যুক্ত করুন'}
            type="submit"
          />
          <button
            type="button"
            className="btn btn-secondary text-white w-44"
            onClick={() => navigate('/dashboard/donor-partner')}
          >
            বাতিল করুন
          </button>
        </div>
      </form>
    </>
  );
}

export default AddDonorPartner;
